import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Alert, Button, FormControlLabel, IconButton, Stack, Switch, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from 'src/api';
import { propertyUtilityIdOf } from 'src/components/property/UtilityPortalCredentials/credentialStatus';
import { useStore } from 'src/stores';

// Direct API calls, not a store model, as the bill ready field does: a store model's run() cancels its own in-flight
// call, and the setup page mounts one of these per utility.
const NO_OPTIONS = {
  cancelToken: {}
};
const stopPropagation = event => event.stopPropagation();

// The title sits as far from its switch as a utility's icon does from the utility's own switch on the setup page.
const TITLE = {
  marginBottom: 0,
  marginLeft: '10px'
};

// A 20px info icon beside the 15px title, as the design has it: the theme's small icon button is 16px, its medium 24px.
const INFO_BUTTON = {
  padding: '2px',
  '& .MuiSvgIcon-root': {
    width: '20px',
    height: '20px'
  }
};

/**
 * Said when a switch saves: the save writes every row of the property's utility, and a page can show more than one
 * of them, so each switch reads itself again.
 */
export const AP_PUSH_SAVED = 'livable:ap-push-saved';

// property-service answers errors in PascalCase: { Error: { ErrorCode, GeneralErrorMessage } }.
const messageOf = err => {
  var _err$response;
  const message = err === null || err === void 0 || (_err$response = err.response) === null || _err$response === void 0 || (_err$response = _err$response.data) === null || _err$response === void 0 || (_err$response = _err$response.Error) === null || _err$response === void 0 ? void 0 : _err$response.GeneralErrorMessage;
  return typeof message === 'string' && message.length <= 200 ? message : 'Could not save AP Push.';
};

/**
 * The switch, titled as a utility is, with what it does behind an info icon, as the saved setting and the choice made
 * before saving both show it.
 */
const ApPushToggle = _ref => {
  let about = _ref.about,
    checked = _ref.checked,
    children = _ref.children,
    className = _ref.className,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    onChange = _ref.onChange,
    testId = _ref.testId;
  return (
    /*#__PURE__*/
    // Contained: on the V2 setup page this sits inside an AccordionSummary, which toggles on any click or key that
    // bubbles up to it.
    React.createElement(Stack, {
      spacing: "4px",
      className: className,
      "data-testid": testId,
      onClick: stopPropagation,
      onKeyDown: stopPropagation,
      onKeyUp: stopPropagation
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      spacing: "4px"
    }, /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Switch, {
        checked: checked,
        disabled: disabled,
        onChange: onChange,
        inputProps: {
          'aria-label': 'AP Push'
        }
      }),
      label: /*#__PURE__*/React.createElement(Typography, {
        variant: "h5",
        component: "span",
        sx: TITLE
      }, "AP Push")
    }), /*#__PURE__*/React.createElement(ExpandedTooltip, {
      type: "grey",
      title: about,
      placement: "top",
      describeChild: true
    }, /*#__PURE__*/React.createElement(IconButton, {
      size: "small",
      "aria-label": "About AP Push",
      sx: INFO_BUTTON
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "info",
      state: "info"
    })))), children)
  );
};
const ABOUT_SAVED = "When on, this utility's bills are pushed to AppFolio as payables. Turning it off doesn't change bills already pushed.";
const ABOUT_NOT_SAVED = "When on, this utility's bills are pushed to AppFolio as payables, from when it's saved.";

/**
 * Whether this utility's bills are pushed to the company's AppFolio as payables (LB-6031). A utility can be billed to
 * residents, enrolled in AP Push, or both. AppFolio companies only. Renders nothing when the setting cannot be read, so
 * a caller without access sees no broken section.
 */
export const ApPushSwitch = observer(_ref2 => {
  let propertyUtilityId = _ref2.propertyUtilityId,
    className = _ref2.className;
  const store = useStore();
  const isAppfolio = store.company.companyFeatureStore.isAppfolio;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    enabled = _useState2[0],
    setEnabled = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    saving = _useState4[0],
    setSaving = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    error = _useState6[0],
    setError = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    readFailed = _useState8[0],
    setReadFailed = _useState8[1];
  // Reads can overlap -- a retry, a save here or beside it -- and only the latest may answer, or an older one landing
  // late would put back what the newer one replaced.
  const latestRead = useRef(0);
  const load = useCallback(async () => {
    const read = ++latestRead.current;
    try {
      var _resp$response;
      const resp = await api.PropertyUtility.getApPush({
        propertyUtilityId
      }, NO_OPTIONS);
      if (read !== latestRead.current) return;
      setEnabled(typeof (resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.enabled) === 'boolean' ? resp.response.enabled : null);
      setReadFailed(false);
    } catch (err) {
      var _err$response2;
      if (read !== latestRead.current) return;
      setEnabled(null);
      // No access, or no such property utility: nothing to show. Anything else is worth a retry.
      setReadFailed(![403, 404].includes(err === null || err === void 0 || (_err$response2 = err.response) === null || _err$response2 === void 0 ? void 0 : _err$response2.status));
    }
  }, [propertyUtilityId]);
  useEffect(() => {
    if (!propertyUtilityId || !isAppfolio) return undefined;
    load();
    window.addEventListener(AP_PUSH_SAVED, load);
    return () => window.removeEventListener(AP_PUSH_SAVED, load);
  }, [propertyUtilityId, isAppfolio, load]);
  if (!isAppfolio || !propertyUtilityId) return null;
  if (readFailed) {
    return /*#__PURE__*/React.createElement(Alert, {
      severity: "warning",
      className: className,
      action: /*#__PURE__*/React.createElement(Button, {
        size: "small",
        onClick: load
      }, "Retry")
    }, "Couldn't load AP Push.");
  }
  if (enabled === null) return null;
  const handleChange = async event => {
    const next = event.target.checked;
    setSaving(true);
    setError('');
    try {
      await api.PropertyUtility.setApPush({
        propertyUtilityId,
        enabled: next
      }, NO_OPTIONS);
      window.dispatchEvent(new Event(AP_PUSH_SAVED));
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  };
  return /*#__PURE__*/React.createElement(ApPushToggle, {
    about: ABOUT_SAVED,
    checked: enabled,
    className: className,
    disabled: saving,
    onChange: handleChange,
    testId: "ap-push-switch"
  }, error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, error) : null);
});

/**
 * AP Push for a utility not saved yet, so there is nothing to save it to. The caller holds the choice on the utility,
 * and turnOnApPushFor turns it on once the utility is saved. AppFolio companies only.
 *
 * Not offered with billing options on: a new utility takes AP Push from the setup it links to when it's saved (LB-6186),
 * and Edit property's save ignores the choice, so the switch would do nothing, on Pro as anywhere (LB-6188).
 */
export const ApPushChoice = observer(_ref3 => {
  let checked = _ref3.checked,
    onChange = _ref3.onChange,
    className = _ref3.className;
  const store = useStore();
  if (!store.company.companyFeatureStore.isAppfolio || store.account.featureFlagsStore.getIsBillingOptionsEnabled) {
    return null;
  }
  return /*#__PURE__*/React.createElement(ApPushToggle, {
    about: ABOUT_NOT_SAVED,
    checked: !!checked,
    className: className,
    onChange: event => onChange(event.target.checked),
    testId: "ap-push-choice"
  });
});

/**
 * Turns AP Push on for the utilities chosen for it before they were saved, now that they have been (LB-6031).
 *
 * AP Push belongs to the property's utility, not to one provider's row of it: the server turns it on for every row of
 * the utility, so one saved row of each chosen utility is enough.
 *
 * @param utilityIds the chosen utilities' utility ids, as they were created
 * @param savedUtilities the property's utilities as read back after the save
 * @returns how many of them could not be turned on
 */
export async function turnOnApPushFor(utilityIds, savedUtilities) {
  if (!utilityIds.size) return 0;
  const seen = new Set();
  const saved = savedUtilities.filter(utility => {
    if (!utility.isFetched || !utilityIds.has(utility.utilityId) || !propertyUtilityIdOf(utility)) return false;
    if (seen.has(utility.utilityId)) return false;
    seen.add(utility.utilityId);
    return true;
  });
  const results = await Promise.allSettled(saved.map(utility => api.PropertyUtility.setApPush({
    propertyUtilityId: propertyUtilityIdOf(utility),
    enabled: true
  }, NO_OPTIONS)));
  window.dispatchEvent(new Event(AP_PUSH_SAVED));
  const notFound = utilityIds.size - saved.length;
  return notFound + results.filter(result => result.status === 'rejected').length;
}